import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ButtonComponent from '../../components/ButtonComponent';
import InputField from '../../components/form/InputField';
import useFormInput from '../../custom-hooks/useFormInput';
import AuthLayout from '../../layouts/AuthLayout';
import { LuBuilding, LuGlobe, LuMapPin, LuUser2 } from 'react-icons/lu';
import {
  ProfileSetupDefault,
  ProfileSetupSchema,
  ProfileSetupType,
} from '../../app-schemas/profile.schema';
import contentMgt from '../../api-services/contents/contents.services';
import { ErrorMessage } from '../../components/ErrorMessage';
import apiClient from '../../api-services/ApiClient';
import profile_api from '../../api-services/profile/profile.services';
import { getUser, storeUser } from '../../app-utils';
import { SITELINK } from '../../app-routes/Links';

// main-component-ui
const ProfileSetup = () => {
  document.body.classList.remove('overflow-hidden');
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [countries, setCountries] = useState<any>([]);
  const [cities, setCities] = useState<any>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [formError, setFormError] = useState<any>();
  const [buttonStyle, setButtonStyle] = useState<string>('fixed');

  useEffect(() => {
    getAllCountries();
    getProximityCities();
  }, []);

  // custom hook to get && validate users input setValidationErrors
  const [formData, handleChange, errors, setValidationErrors, handleCustomChange] = useFormInput(
    ProfileSetupDefault,
    ProfileSetupSchema,
  );

  const inputFocus = () => setButtonStyle('relative');
  const inputBlur = () => setButtonStyle('fixed');

  const getAllCountries = async () => {
    setLoading(true);
    const result = await contentMgt.getCountries();
    setLoading(false);

    if (Array.isArray(result)) {
      const options = result;
      setCountries(options);
    } else {
      setErrorMsg('error fetching country');
      setFormError('error fetching country');
    }
  };

  const getProximityCities = async () => {
    try {
      const result = await apiClient.get('/profile/proximityCities');
      setCities(result.data.data);
    } catch (err) {
      alert('error');
    }
  };

  const handleOnboarding = async (e: FormEvent) => {
    e.preventDefault();
    const { success, data, error } = ProfileSetupSchema.safeParse(formData);

    if (success) {
      setLoading(true);

      try {
        const res = await profile_api.UpdateProfile(data);

        if (res?.data?.message === 'profile updated') {
          let me = getUser();
          me.username = data.username;
          me.residency_city_id = data.cityId;
          me.nationality_country_id = data.nationalityId;

          storeUser(me);

          navigate(SITELINK.HOME);
        } else {
          setErrorMsg(res?.response?.data?.message);
        }
      } catch (error) {
        setErrorMsg('An error occured!');
      } finally {
        setLoading(false);
      }
    } else {
      const newErrors: Partial<Record<keyof ProfileSetupType, string>> = {};
      error.errors.forEach((err) => {
        newErrors[err.path[0] as keyof ProfileSetupType] = err.message;
      });

      setValidationErrors(newErrors);
      setFormError(newErrors);
    }
  };

  return (
    <section className="mt-8">
      <AuthLayout title="Catapot | Profile Setup">
        {/* hero-text area */}
        <div className="flex flex-col items-start mt-2">
          <h4 className="text-[20px] font-[400] text-black madimi-one flex gap-1 items-center">
            Let's get your profile setup
          </h4>
          <p className="text-[12px] font-[700] text-black flex gap-1 items-center pr-24">
            complete all required fields
          </p>
        </div>

        <div className="text-red-600 text-xs mt-3 col-span-full text-center">{errorMsg}</div>

        {/* complete form area */}
        <form className="grid grid-cols-2 gap-3 mt-5" onSubmit={handleOnboarding}>
          <div className="relative w-full col-span-full">
            <InputField
              className="col-span-full text-[12px] rounded-lg"
              type="text"
              id="username"
              placeholder="Nickname"
              value={formData.username.replace(/[^\w\s]|_/g, '').replace(/\s+/g, '')}
              onChange={handleChange}
              onFocus={inputFocus}
              onBlur={inputBlur}
              required
              icon={LuUser2}
              error={errors?.username}
            />
          </div>
          <div className="relative w-full col-span-full">
            <div className="relative w-full col-span-full">
              <span className="absolute p-[5px] rounded-[50px] top-[8px] left-[7px] text-xl text-[#98A1B0]">
                <LuGlobe />
              </span>
              <select
                className={`focus:outline-none focus:shadow-outline border border-input border-gray-300 rounded-md pl-9 px-4 py-3 w-full text-[12px] text-gray-700 focus:shadow-lg focus:border-[#58595c] ${
                  !!formError?.nationality && 'border-red-600'
                }`}
                onChange={(e) => handleCustomChange('nationalityId', Number(e.target.value))}
                onFocus={inputFocus}
                onBlur={inputBlur}
              >
                <option value="" disabled selected>
                  Select your Nationality
                </option>
                {countries.map((item: any) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {!!formError?.nationality && <ErrorMessage message={formError?.nationality} />}
            </div>
          </div>
          <div className="relative w-full col-span-full mt-3 mb-3">
            <hr />
          </div>
          <div className="relative w-full col-span-full">
            <span className="absolute p-[5px] rounded-[50px] top-[8px] left-[7px] text-xl text-[#98A1B0]">
              <LuMapPin />
            </span>
            <div
              className={`focus:outline-none focus:shadow-outline border border-input border-gray-300 rounded-md pl-9 px-6 ml-1 py-3 w-full text-[12px] text-gray-700 focus:shadow-lg focus:border-[#58595c]`}
            >
              {cities[0]?.state_name}
              {cities[0]?.state_name && ', '}
              {cities[0]?.country_name}
            </div>
          </div>
          <div className="relative w-full col-span-full">
            <div className="relative w-full col-span-full">
              <span className="absolute p-[5px] rounded-[50px] top-[8px] left-[7px] text-xl text-[#98A1B0]">
                <LuBuilding />
              </span>
              <select
                className={`focus:outline-none focus:shadow-outline border border-input border-gray-300 rounded-md pl-9 px-4 py-3 w-full text-[12px] text-gray-700 focus:shadow-lg focus:border-[#58595c] ${
                  !!formError?.nationality && 'border-red-600'
                }`}
                onChange={(e) => handleCustomChange('cityId', Number(e.target.value))}
                onFocus={inputFocus}
                onBlur={inputBlur}
              >
                <option value="" disabled selected>
                  Select your closest city in Nigeria
                </option>
                {cities.map((item: any) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>

              {!!formError?.nationality && <ErrorMessage message={formError?.nationality} />}
            </div>
          </div>
          {/* action btn */}{' '}
          <div className={`${buttonStyle} right-0 left-0 bottom-0 bg-white w-full col-span-full`}>
            <div className={`${buttonStyle === 'fixed' && 'px-6'} w-full`}>
              <ButtonComponent
                className="mb-5 w-[200px] mt-2 text-sm  py-3 rounded-lg text-white"
                type="submit"
                disabled={loading}
                loading={loading}
              >
                Continue
              </ButtonComponent>

              <p className="text-[10px] font-[400] text-center items-center mb-10 px-8">
                Click continue to confirm you have comfortable with the required field you have
                completed
              </p>
            </div>
          </div>
        </form>
      </AuthLayout>
    </section>
  );
};

export default ProfileSetup;
